import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { AccessibilityWidget } from "@/components/AccessibilityWidget";
import { CTAContactForm } from "@/components/CTAContactForm";
import { RelatedServices } from "@/components/RelatedServices";
import { Phone, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/services/commercial-masonry-dover-morris-county-nj.webp";
import commercialWayne from "@/assets/commercial-masonry-north-jersey-wayne.webp";
import commercialVerona from "@/assets/commercial-masonry-verona-nj.webp";
import cmuDiagram from "@/assets/diagram-cmu-masonry-installation-totowa-nj.webp";
import cmuClifton from "@/assets/commercial-cmu-masonry-clifton-nj.webp";
import commercialBloomfield from "@/assets/commercial-masonry-bloomfield-nj.webp";

const masonryTypes = [
  {
    type: "Brick Masonry",
    role: "Veneer and aesthetic facades",
    use: "Street-level storefronts, mixed-use building exteriors, decorative accent walls",
  },
  {
    type: "CMU Block Masonry",
    role: "Load-bearing structural walls",
    use: "Warehouses, foundation walls, fire-rated partitions, below-grade construction",
  },
  {
    type: "Stone Masonry",
    role: "Decorative or retaining applications",
    use: "Retaining walls, landscape features, decorative facades, monument signs",
  },
];

const processSteps = [
  {
    step: "1",
    title: "Site Assessment",
    description: "Review soil conditions, drainage, existing structural concerns, and permit requirements before any work begins.",
  },
  {
    step: "2",
    title: "Scope & Permit Review",
    description: "Accurate scope review following MCAA estimating best practices. Handle permit applications through your municipality's building department.",
  },
  {
    step: "3",
    title: "Material Specification",
    description: "Select CMU, brick, stone, or poured concrete based on structural requirements, ASTM standards, and project specifications.",
  },
  {
    step: "4",
    title: "Safety Compliance",
    description: "Document OSHA silica compliance (29 CFR 1926.1153), wall bracing, and scaffolding requirements before the first cut.",
  },
  {
    step: "5",
    title: "Construction",
    description: "Licensed journeyman masons execute structural load calculations, reinforcement placement, and code-compliant construction.",
  },
  {
    step: "6",
    title: "Inspection & Closeout",
    description: "Final inspection coordination with local building officials. Documentation of completed work for your records.",
  },
];

const faqs = [
  {
    question: "What does CMU mean in construction, and do I need it for my North Jersey project?",
    answer: "CMU stands for concrete masonry unit, a standard block used for load-bearing walls in commercial construction. Most commercial buildings in North Jersey require CMU for structural strength and code compliance, especially for walls that carry floor or roof loads.",
  },
  {
    question: "What are the three types of masonry used in commercial buildings?",
    answer: "The three main types are brick masonry, concrete block (CMU) masonry, and stone masonry. Brick handles veneer and facade work. CMU handles structural load-bearing walls. Stone is used for decorative or retaining applications.",
  },
  {
    question: "Why does commercial masonry cost more than other construction methods?",
    answer: "Licensed tradespeople, ASTM-spec materials, equipment, and permit fees all factor into the cost. Commercial masonry also involves structural engineering requirements that residential work does not. The durability and longevity of a well-built masonry structure offset the upfront investment over time.",
  },
  {
    question: "How do I know if a North Jersey masonry contractor is qualified for a commercial job?",
    answer: "Look for NJ state contractor registration, commercial references, and documented OSHA safety practices. MCAA membership or certified mason credentials are also a strong indicator of professional standards. Ask specifically about experience with your project type, whether that's a warehouse, school, or retail center.",
  },
  {
    question: "What is the first step to start a commercial masonry project in North Jersey?",
    answer: "Schedule a site assessment. We review structural needs, permit requirements, site access, and material specifications before any work begins. That process gives you an accurate scope and prevents surprises during construction.",
  },
  {
    question: "Do commercial masonry jobs in North Jersey require permits?",
    answer: "Yes. Structural masonry work requires permits through your municipality's building department. A licensed contractor handles the permit application process and knows what each county requires before work can start.",
  },
];

export default function CommercialMasonry() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Commercial Masonry & Concrete Totowa, NJ | Masonry Pro</title>
        <meta name="description" content="Commercial masonry and concrete services across Bergen, Passaic, Morris, and Essex Counties. CMU walls, structural repairs, concrete flatwork. Licensed." />
        <meta property="og:description" content="Commercial masonry and concrete services across Bergen, Passaic, Morris, and Essex Counties. CMU walls, structural repairs, concrete flatwork. Licensed." />
        <meta property="og:title" content="Commercial Masonry & Concrete Totowa, NJ | Masonry Pro" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Commercial Masonry & Concrete",
            description: "Commercial masonry and concrete services including CMU walls, structural repairs, and concrete flatwork in North Jersey.",
            url: "/masonry-services/commercial-masonry",
            image: "https://masonry-pro.com/og/commercial-masonry.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/commercial-masonry.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/commercial-masonry.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Commercial masonry construction in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl mx-auto text-center">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Commercial Masonry & Concrete in Totowa, NJ Serving{" "}
                  <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>North Jersey</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.125rem', marginBottom: '1rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Built to Last
                </p>
                <div style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.125rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }} className="space-y-4">
                  <p>
                    In North Jersey, commercial buildings face tough winters, freeze-thaw cycles, and strict building codes. Commercial masonry and concrete covers CMU wall construction, concrete slabs, block foundations, and structural repairs.
                  </p>
                  <p>
                    We work on warehouses, retail strip centers, municipal buildings, schools, and parking structures. Most projects start with a site assessment and permit review.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* What Is Commercial Masonry — Row 1: Text + Image */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    <span className="text-gradient-gold">What Is Commercial Masonry & Concrete</span> in North Jersey?
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                    <p>
                      Commercial masonry and concrete covers the construction, repair, and renovation of structural walls, slabs, and foundations using brick, block, stone, or poured concrete. In North Jersey, commercial masonry contractors work on warehouses, schools, retail centers, and mixed-use buildings. Commercial clients get the same crew and standards as every other job from our <Link to="/" className="text-secondary hover:underline">Totowa, NJ masonry contractor</Link> team.
                    </p>
                    <p>Licensed masons follow state and local building codes on every project.</p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <img
                    src={commercialWayne}
                    alt="Commercial masonry project in Wayne, North Jersey by Masonry Pro Construction"
                    className="w-full h-auto object-cover rounded-xl"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Row 2: 3 Cards + Image (alternating) */}
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div className="order-2 lg:order-1 rounded-xl overflow-hidden">
                  <img
                    src={commercialVerona}
                    alt="Commercial masonry work in Verona, NJ by Masonry Pro Construction"
                    className="w-full h-auto object-cover rounded-xl"
                    loading="lazy"
                  />
                </div>
                <div className="order-1 lg:order-2 space-y-4">
                  {[
                    { label: "Common Services", value: "CMU block walls, concrete flatwork, structural masonry repair" },
                    { label: "Materials Used", value: "Concrete masonry units (CMU), reinforced block, poured concrete" },
                    { label: "Key Compliance", value: "NJ building code, ASTM material standards, OSHA safety rules" },
                  ].map((item) => (
                    <div key={item.label} className="bg-card rounded-xl p-5 border border-border">
                      <p className="text-secondary font-semibold mb-1">{item.label}</p>
                      <p className="text-muted-foreground text-sm">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* CMU Walls — Row 1: Text + Placeholder Image */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    <span className="text-gradient-gold">CMU Walls</span> Are the Foundation of Commercial Construction
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                    <p>
                      Concrete masonry units are the standard choice for load-bearing wall construction in North Jersey commercial projects. One material handles fire resistance, sound control, and structural load requirements at the same time.
                    </p>
                    <p>
                      Wayne and Passaic County projects frequently spec reinforced CMU for foundation and below-grade applications. For material standards, we follow ASTM International specifications throughout every project.
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <img
                    src={cmuClifton}
                    alt="CMU block wall construction for commercial project in Clifton, NJ"
                    className="w-full h-auto object-cover rounded-xl"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* CMU Specs Card + Placeholder Image (alternating) */}
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div className="order-2 lg:order-1 rounded-xl overflow-hidden">
                  <img
                    src={cmuDiagram}
                    alt="CMU masonry installation diagram showing foundation, flashing, reinforcement, and block wall construction in Totowa, NJ"
                    className="w-full h-auto object-cover rounded-xl"
                    loading="lazy"
                  />
                </div>
                <div className="order-1 lg:order-2">
                  <div className="rounded-xl border-2 border-secondary/30 bg-card p-8">
                    <h3 className="text-xl font-bold mb-4 text-secondary">CMU Technical Specs</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
                        Minimum compressive strength: 2,000 psi per ASTM C90
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
                        Low-absorption, high-density profile for below-grade walls
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
                        Fire resistance, sound control, and structural load capacity
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
                        Standards per Concrete Masonry & Hardscapes Association
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Three Types of Masonry */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold mb-4 text-center">
                  Three Types of Masonry Used in{" "}
                  <span className="text-gradient-gold">North Jersey Commercial Projects</span>
                </h2>
                <p className="text-center text-muted-foreground mb-10 max-w-3xl mx-auto">
                  Not every commercial project calls for the same material. Understanding the differences helps match the right approach to your structural and aesthetic needs.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {masonryTypes.map((item) => (
                    <div key={item.type} className="bg-background rounded-xl p-6 border border-border">
                      <h3 className="text-lg font-bold mb-2">{item.type}</h3>
                      <p className="text-secondary font-medium text-sm mb-2">{item.role}</p>
                      <p className="text-muted-foreground text-sm">{item.use}</p>
                    </div>
                  ))}
                </div>
                <p className="text-muted-foreground text-sm mt-6 text-center max-w-3xl mx-auto">
                  Bergen County and Essex County commercial corridors, including areas like Montclair and Ridgewood, frequently combine CMU structure with brick veneer. That combination works well for mixed-use streetfronts where structural performance and street-level appearance both matter.
                </p>
              </div>
            </div>
          </section>

          {/* Skilled Tradespeople - Full Width Text */}
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold mb-6">
                  Commercial Masonry Takes{" "}
                  <span className="text-gradient-gold">Skilled, Licensed Tradespeople</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Commercial masonry is not general labor work. It involves structural load calculations, reinforcement placement, and code compliance at every stage.
                  </p>
                  <p>
                    New Jersey requires masonry contractors to carry state contractor registration and general liability insurance. The Masonry Society's TMS 402/602 standards govern structural masonry design and are referenced by the International Building Code.
                  </p>
                  <p>
                    Larger commercial projects across North Jersey often require certified masonry inspectors. We work with journeyman masons who understand both the code requirements and the craft behind them.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Credentials Card + Image (alternating) */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div className="rounded-xl border-2 border-secondary/30 bg-background p-8">
                  <h3 className="text-xl font-bold mb-4 text-secondary">Credentials & Standards</h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
                      NJ state contractor registration
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
                      General liability insurance
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
                      TMS 402/602 structural masonry standards
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
                      OSHA silica compliance (29 CFR 1926.1153)
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
                      Journeyman masons on every crew
                    </li>
                  </ul>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <img
                    src={commercialBloomfield}
                    alt="Commercial brick masonry building exterior in Bloomfield, NJ"
                    className="w-full h-auto object-cover rounded-xl"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Process Steps */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold mb-4 text-center">
                  Our Commercial Masonry{" "}
                  <span className="text-gradient-gold">Process</span>
                </h2>
                <p className="text-center text-muted-foreground mb-10 max-w-3xl mx-auto">
                  Every commercial masonry job starts with a site assessment. Before any work begins, we review soil conditions, drainage, existing structural concerns, and permit requirements.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {processSteps.map((step) => (
                    <div key={step.step} className="bg-background rounded-xl p-6 border border-border relative">
                      <div className="w-10 h-10 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center font-bold text-lg mb-4">
                        {step.step}
                      </div>
                      <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                      <p className="text-muted-foreground text-sm">{step.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Safety Compliance */}
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">
                  Job Site Safety Compliance Is{" "}
                  <span className="text-gradient-gold">Non-Negotiable</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed text-center mb-8">
                  <p>
                    Professional commercial masonry crews follow OSHA standards for silica dust control, wall bracing, and scaffolding. That protects workers and limits owner liability.
                  </p>
                  <p>
                    Per OSHA 29 CFR 1926.1153, all contractors cutting or grinding concrete or masonry must implement dust controls and maintain written exposure control plans.
                  </p>
                  <p>
                    Bergen, Passaic, and Morris County projects are subject to NJ OSHA inspections. We document silica compliance before the first cut on every job.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Frequently Asked{" "}
                <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="max-w-3xl mx-auto space-y-4">
                {faqs.map((faq, index) => (
                  <details
                    key={faq.question}
                    className="group bg-background rounded-xl border border-border"
                    open={index === 0}
                  >
                    <summary className="flex items-center justify-between cursor-pointer p-6 text-lg font-bold hover:text-secondary transition-colors list-none [&::-webkit-details-marker]:hidden">
                      {faq.question}
                      <span className="ml-4 text-secondary text-xl group-open:rotate-45 transition-transform">+</span>
                    </summary>
                    <div className="px-6 pb-6">
                      <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="masonry" />

          {/* CTA Section */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto items-start">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Get a Free{" "}
                    <span className="text-gradient-gold">Commercial Estimate</span>
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      Ready to discuss your commercial masonry project? Call or text us at{" "}
                      <a
                        href="tel:+19737252395"
                        className="text-secondary hover:underline font-semibold"
                      >
                        (973) 725-2395
                      </a>{" "}
                      or fill out the form. We serve property owners and managers across Bergen, Passaic, Morris, and Essex counties.
                    </p>
                    <p>
                      Warehouses, retail centers, municipal buildings, schools, and parking structures: we handle it all with licensed crews and code-compliant construction.
                    </p>
                  </div>
                </div>
                <CTAContactForm serviceName="Commercial Masonry" />
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <AccessibilityWidget />
      </div>
    </>
  );
}
